import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { ButtonFooter, FormLayout, FormRow as Row, FormSection as Section } from '~/components';
import { OptionType } from '~/features/types';
import { Select } from '../../../components';
import { useCustomForm } from '../../../hooks/useCustomForm';
import { emptyForm } from './constants';
import { AOIFormValues, aoiSchema, CreateAOIFormValues } from './types';
const TAGS = CT.aoi.form;

interface Props {
  add: (formValues: CreateAOIFormValues) => void;
  AOIOptions: OptionType[];
  AOISpecialtyOptions: OptionType[];
  formValues: AOIFormValues;
  getSpecOptions: () => void;
  handleClose: () => void;
  setSpecId: (value: string) => void;
  // [MCV-1207] Message to render inline when the last submit attempt failed.
  submitError?: string | undefined;
}

export const AOIForm = (props: Props) => {
  const {
    add,
    AOIOptions,
    AOISpecialtyOptions,
    formValues,
    getSpecOptions,
    handleClose,
    setSpecId,
    submitError,
  } = props;

  const methods = useCustomForm<AOIFormValues>({
    resolver: zodResolver(aoiSchema),
    defaultValues: formValues,
  });
  const { watch, handleSubmit, setValue } = methods;

  useEffect(() => {
    if (AOISpecialtyOptions.length == 0) {
      getSpecOptions();
    }
  }, [AOISpecialtyOptions]);

  const spec = watch('specSelect');

  // [MCV-876] Clear the AOI selection whenever the specialty changes so the user
  // explicitly re-picks. Without this, the previous AOI's value silently stays in
  // RHF state even though the dropdown shows options from the new specialty.
  useEffect(() => {
    if (spec.label) {
      setValue('aoiSelect', emptyForm.aoiSelect);
      setSpecId(spec.value);
    }
  }, [spec.value]);

  const onSubmitSuccess = (values: CreateAOIFormValues) => {
    add(values as CreateAOIFormValues);
  };

  // [MCV-873] Do NOT close the form on submit validation error — user input must be preserved.
  const onSubmitError = (err: unknown) => {
    console.debug('AOI Submit Error: ', err);
  };

  return (
    <FormProvider {...methods}>
      <form data-cy={TAGS.form} onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          {/* [MCV-1207] Inline submit error banner. Replaces the generic
              corner toast so the user sees the failure attached to the form. */}
          {submitError && (
            <div
              role="alert"
              data-cy={TAGS.submitError}
              className="w-full rounded-control border border-error-t-600 bg-error-t-900 p-3 text-sm text-error-300"
            >
              {submitError}
            </div>
          )}
          {/* [MCV-873] Use Select's own `label` + `required` props rather than
              an outer headless `<Label>` so the `*` indicator renders and
              inline field errors appear next to the relevant field. */}
          <Section componentsPerLine={1}>
            <Row>
              <Select
                name={'specSelect'}
                label={'Specialty'}
                required
                options={AOISpecialtyOptions}
                cyTag={TAGS.specSelect}
              />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <Select
                name={'aoiSelect'}
                label={'Area of Interest'}
                required
                options={AOIOptions}
                cyTag={TAGS.aoiSelect}
              />
            </Row>
          </Section>
          <ButtonFooter
            secondaryButton={{
              label: 'Cancel',
              cyTag: TAGS.cancelBtn,
              onClick: handleClose,
            }}
            primaryButton={{ label: 'Add', cyTag: TAGS.submitBtn, isSubmit: true }}
          />
        </FormLayout>
      </form>
    </FormProvider>
  );
};
